import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { CommonState } from "@obg/ngx.common";
import { AppError, DgaAuthTransactionSelector, ExperianData, ProductType } from "@obg/ngx.core";
import { RouteSelector } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { asyncScheduler, of } from "rxjs";
import { catchError, map, observeOn, switchMap, withLatestFrom } from "rxjs/operators";

import { REGISTRATION_ACTION_TYPE } from "../../registration/registration.action";
import { RegistrationFeatureState } from "../../state";
import { Credential, RegistrationResponse, UserIdentity } from "../registration.model";

import { DGA_REGISTRATION_ACTION_TYPE, DgaRegistrationAction } from "./dga-registration.action";
import { DgaRegistrationFormData, DgaRegistrationPayload } from "./dga-registration.model";
import { DgaRegistrationSelector } from "./dga-registration.selector";
import { DgaRegistrationService } from "./dga-registration.service";

@Injectable()
export class DgaRegistrationEffect {
	getRegistrationToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DGA_REGISTRATION_ACTION_TYPE.getRegistrationToken),
			map((x: ActionWithPayload<DgaRegistrationPayload>) => x.payload),
			withLatestFrom(
				this.store.select(this.dgaAuthTransactionSelector.getAuthenticationToken()),
				this.store.select(this.dgaAuthTransactionSelector.getExperianData()).pipe(ignoreNil()),
				this.store.select(this.dgaRegistrationSelector.getRegistrationFormData()).pipe(ignoreNil()),
				this.store.select<{ product: ProductType | undefined } | undefined>(RouteSelector.getQueryParams)
			),
			switchMap(([payload, token, expData, formData, params]) => {
				const authToken = token || payload.credential.token;
				return this.service
					.registerDgaCustomer(this.buildDgaPayload(payload, formData, expData, authToken), params?.product)
					.pipe(
						map((response: RegistrationResponse) =>
							this.action.getRegistrationTokenSuccess(response, this.getCredentials(formData.credential))
						),
						catchError((response: AppError) => of(this.action.getRegistrationTokenFail(response)))
					);
			})
		)
	);

	clear$ = createEffect(() =>
		this.actions$.pipe(
			ofType(REGISTRATION_ACTION_TYPE.registrationSuccess),
			observeOn(asyncScheduler),
			map(() => this.action.resetRegistrationState())
		)
	);

	constructor(
		private actions$: Actions,
		private store: Store<RegistrationFeatureState & CommonState>,
		private action: DgaRegistrationAction,
		private dgaAuthTransactionSelector: DgaAuthTransactionSelector,
		private dgaRegistrationSelector: DgaRegistrationSelector,
		private service: DgaRegistrationService
	) {}

	private buildDgaPayload(
		payload: DgaRegistrationPayload,
		dgaFormData: DgaRegistrationFormData,
		expData: ExperianData,
		token: string
	): DgaRegistrationPayload {
		const { additionalDetails, credential, consents } = dgaFormData;
		const { city, zipCode, street } = payload.additionalDetails;

		const { cpr } = payload.person;

		const fullAdditionalDetails = { ...additionalDetails, city, street, zipCode };
		const fullCredentials = { ...credential, token };
		const fullPerson = { ...payload.person, cpr, dateOfBirth: expData.dateOfBirth };

		return {
			...payload,
			consents,
			person: fullPerson,
			additionalDetails: fullAdditionalDetails,
			credential: fullCredentials,
		};
	}

	private getCredentials(credential: UserIdentity): Credential | undefined {
		const { username, email, password } = credential;
		return password ? { username, email, password } : undefined;
	}
}
